import type { LucideIcon } from "lucide-react";
import { Cpu, Building2, FolderOpen, Rocket, CreditCard } from "lucide-react";
import { Badge } from "../../base/badge";
import { cn } from "@charphq/ui/lib/utils";

export type EntityKind = "device" | "customer" | "project" | "deployment" | "simcard";

interface EntityTypeMeta {
  label: string;
  Icon: LucideIcon;
  /** Badge color classes used for the per-card type badge. */
  badgeClass: string;
}

/**
 * Single source of truth for how each searchable entity type is labelled and
 * coloured. Reused by both the universal-search section headers and the
 * per-card type badge so the two never drift.
 */
export const entityTypeMeta: Record<EntityKind, EntityTypeMeta> = {
  device: {
    label: "Device",
    Icon: Cpu,
    badgeClass: "bg-status-info-soft text-status-info",
  },
  customer: {
    label: "Customer",
    Icon: Building2,
    badgeClass: "bg-humidity-soft text-humidity",
  },
  project: {
    label: "Project",
    Icon: FolderOpen,
    badgeClass: "bg-light-soft text-light",
  },
  deployment: {
    label: "Deployment",
    Icon: Rocket,
    badgeClass: "bg-status-ok-soft text-status-ok",
  },
  simcard: {
    label: "SIM Card",
    Icon: CreditCard,
    badgeClass: "bg-muted text-muted-foreground",
  },
};

/** Small coloured badge (icon + label) identifying an entity's type. */
export function EntityTypeBadge({ kind, className }: { kind: EntityKind; className?: string }) {
  const { label, Icon, badgeClass } = entityTypeMeta[kind];
  return (
    <Badge
      variant="outline"
      className={cn("gap-1 border-transparent text-xs", badgeClass, className)}
    >
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}
